import { useState } from "react";
import { format, isToday, differenceInDays, parseISO, getDay, startOfDay } from "date-fns";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ScheduleItem } from "@/types/schedule";
import type { CleaningTaskWithRelations } from "@/types/cleaning";

export interface UnifiedCalendarGridProps {
  properties: { id: string; name: string; address?: string | null; shortAddress?: string | null; property_photo_url?: string | null }[];
  visibleDays: Date[];
  /** Start of the visible window (for nights-based bar span) */
  windowStart?: Date;
  byPropertyId: Record<string, ScheduleItem[]>;
  onReservationClick: (r: Record<string, unknown>, propertyId: string, propertyName: string) => void;
  onTaskClick: (t: CleaningTaskWithRelations, propertyId: string, propertyName: string) => void;
  onTurnoverNeededClick: (r: Record<string, unknown>, propertyId: string, propertyName: string) => void;
  /** Called when a day is clicked (header or cell). date + propertyId (null if header). */
  onDayClick: (date: Date, propertyId: string | null) => void;
  onAddTaskOnDate?: (date: Date, propertyId: string | null) => void;
  isLoading: boolean;
  isPropsLoading: boolean;
}

const ROW_HEIGHT = 72;
/** Thin reservation bar (match single-property month/week view) */
const RESERVATION_BAR_HEIGHT = 24;
/** Min width per day column when horizontal scroll is needed */
const DAY_COLUMN_MIN_PX = 64;

/** One row per property: [Left property card column] + [scrollable date grid]. */
export function UnifiedCalendarGrid({
  properties,
  visibleDays,
  windowStart,
  byPropertyId,
  onReservationClick,
  onTaskClick,
  onTurnoverNeededClick,
  onDayClick,
  onAddTaskOnDate,
  isLoading,
  isPropsLoading,
}: UnifiedCalendarGridProps) {
  const numCols = visibleDays.length;
  const windowStartDate = windowStart ?? (visibleDays[0] ? startOfDay(visibleDays[0]) : new Date());
  const addTaskOnDate = onAddTaskOnDate ?? onDayClick;

  if (properties.length === 0 && !isPropsLoading) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Add a property to see the calendar.
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex w-full min-w-0 border border-border rounded-lg overflow-hidden bg-background">
        {/* Sticky left column: Property header + one row per property (photo, name, address) */}
        <div
          className="sticky left-0 z-20 shrink-0 border-r border-border bg-background w-[min(340px,28vw)] min-w-[200px] max-w-[340px]"
        >
          <div className="border-b border-border py-2 px-3 text-xs font-medium text-muted-foreground">
            Property
          </div>
          {properties.length === 0 && !isPropsLoading ? null : (
            <div className="divide-y divide-border">
              {properties.map((prop) => (
                <div
                  key={prop.id}
                  className="flex items-center gap-3 px-3 py-2 border-border"
                  style={{ minHeight: ROW_HEIGHT }}
                >
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted shrink-0">
                    {prop.property_photo_url ? (
                      <img src={prop.property_photo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">—</div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground truncate">{prop.name}</p>
                    <p className="text-xs text-muted-foreground truncate" title={prop.shortAddress || prop.address || undefined}>
                      {prop.shortAddress || prop.address || "—"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Date grid: fills width with even columns (max 10 days); scrolls horizontally when narrow */}
        <div className="flex-1 min-w-0 overflow-x-auto overflow-y-hidden">
          {isLoading && Object.keys(byPropertyId).length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm">
              Loading schedule…
            </div>
          ) : (
            <div
              className="shrink-0 min-h-full w-full"
              style={{ minWidth: numCols * DAY_COLUMN_MIN_PX }}
            >
              {/* Date header row - even columns */}
              <div
                className="grid border-b border-border bg-muted/50 shrink-0"
                style={{ gridTemplateColumns: `repeat(${numCols}, minmax(0, 1fr))` }}
              >
                {visibleDays.map((d) => {
                  const isWeekend = getDay(d) === 0 || getDay(d) === 6;
                  return (
                    <button
                      key={d.toISOString()}
                      type="button"
                      className={cn(
                        "border-r border-border px-1 py-1.5 text-center text-xs font-medium cursor-pointer transition-colors hover:bg-muted min-w-0",
                        isToday(d) && "bg-primary/10 text-primary",
                        isWeekend && "bg-muted/30"
                      )}
                      onClick={() => onDayClick(d, null)}
                      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onDayClick(d, null)}
                    >
                      {format(d, "EEE d")}
                    </button>
                  );
                })}
              </div>

              {properties.map((prop) => {
            const items = byPropertyId[prop.id] ?? [];
            const reservations = items
              .filter((i) => i.type === "reservation" && !i.isBlocked)
              .sort((a, b) => a.start.localeCompare(b.start));
            const taskAndNeeded = items.filter((i) => i.type === "task" || i.type === "turnover_needed" || i.type === "vendor_visit");
            const checkoutDateToTask = new Map<string | number, ScheduleItem>();
            const dateToAssigneeName: Record<string, string> = {};
            taskAndNeeded.forEach((item) => {
              const dayStr = item.start.slice(0, 10);
              if (item.type === "task") {
                if (item.assignedToName) dateToAssigneeName[dayStr] = item.assignedToName;
                const resId = item.relatedReservationId ?? item.id;
                checkoutDateToTask.set(dayStr, item);
              }
              if (item.type === "vendor_visit") {
                checkoutDateToTask.set(dayStr, item);
              }
            });
            const checkoutDateToNeeded = new Set(
              taskAndNeeded.filter((i) => i.type === "turnover_needed").map((i) => i.start.slice(0, 10))
            );
            const standaloneTasks = taskAndNeeded.filter((item) => {
              const dayStr = item.start.slice(0, 10);
              const isReservationEndDate = reservations.some((r) => r.end.slice(0, 10) === dayStr);
              return !isReservationEndDate;
            });

            // Nights-based bar positions: barStart = check-in, barEndExclusive = check-out (do not include checkout day)
            const bars = reservations.map((r) => {
              const barStartStr = r.start.slice(0, 10);
              const barEndExclusiveStr = r.end.slice(0, 10); // checkout day = first day NOT booked
              const barStart = parseISO(barStartStr);
              const barEndExclusive = parseISO(barEndExclusiveStr);
              let startIdx = Math.max(0, differenceInDays(barStart, windowStartDate));
              let endIdxExclusive = Math.min(numCols, differenceInDays(barEndExclusive, windowStartDate));
              if (startIdx >= numCols || endIdxExclusive <= 0) return null;
              startIdx = Math.min(startIdx, numCols - 1);
              endIdxExclusive = Math.max(endIdxExclusive, startIdx + 1);
              const span = Math.max(1, endIdxExclusive - startIdx);
              const leftPct = (startIdx / numCols) * 100;
              const widthPct = (span / numCols) * 100;
              const payload = r.reservationPayload as {
                guest_name?: string | null;
                start_date?: string;
                end_date?: string;
                source?: string | null;
                sourceLabel?: string | null;
              };
              const nights =
                payload?.start_date && payload?.end_date
                  ? Math.max(1, differenceInDays(parseISO(payload.end_date), parseISO(payload.start_date)))
                  : null;
              const sourceLabel = r.sourceLabel || payload?.source || "Booking";
              const checkoutStr = r.end.slice(0, 10);
              const hasTaskOnCheckout = checkoutDateToTask.has(checkoutStr);
              const needsTurnoverOnCheckout = checkoutDateToNeeded.has(checkoutStr);
              const assigneeOnEnd = dateToAssigneeName[checkoutStr];
              /** iCal/synced (e.g. Airbnb, VRBO) = grey bar; Manual = teal */
              const isIcalReservation = !!sourceLabel && sourceLabel !== "Manual";
              return {
                id: r.id,
                leftPct,
                widthPct,
                payload,
                nights,
                sourceLabel,
                checkoutStr,
                hasTaskOnCheckout,
                needsTurnoverOnCheckout,
                assigneeOnEnd,
                reservation: r,
                isIcalReservation,
              };
            }).filter(Boolean) as Array<{
              id: string;
              leftPct: number;
              widthPct: number;
              payload: { guest_name?: string | null; start_date?: string; end_date?: string; source?: string | null };
              nights: number | null;
              sourceLabel: string;
              checkoutStr: string;
              hasTaskOnCheckout: boolean;
              needsTurnoverOnCheckout: boolean;
              assigneeOnEnd?: string | null;
              reservation: ScheduleItem;
              isIcalReservation: boolean;
            }>;

            return (
              <div
                key={prop.id}
                className="border-b border-border"
                style={{ minHeight: ROW_HEIGHT }}
              >
                <div
                  className="relative grid gap-0 min-h-[72px]"
                  style={{ gridTemplateColumns: `repeat(${numCols}, minmax(0, 1fr))` }}
                >
                  {/* Day cell backdrops (clickable) */}
                  {visibleDays.map((d) => {
                    const isWeekend = getDay(d) === 0 || getDay(d) === 6;
                    return (
                      <DayCell
                        key={d.toISOString()}
                        date={d}
                        isWeekend={isWeekend}
                        onClick={() => addTaskOnDate(d, prop.id)}
                      />
                    );
                  })}
                  {/* Thin reservation bar lane (matches single-property calendar) */}
                  <div
                    className="absolute pointer-events-none flex items-stretch px-0.5"
                    style={{ top: 4, left: 0, right: 0, height: RESERVATION_BAR_HEIGHT }}
                  >
                    <div className="relative flex-1 min-w-0 h-full">
                      {bars.map((bar) => (
                        <Tooltip key={bar.id}>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className={cn(
                                "absolute rounded-sm text-[9px] sm:text-[10px] font-medium truncate px-1 py-0.5 flex items-center justify-between gap-0.5 min-w-0 overflow-hidden pointer-events-auto h-full transition-colors inset-y-0",
                                bar.isIcalReservation
                                  ? "bg-gray-400/90 hover:bg-gray-500 text-white"
                                  : "bg-teal-500/90 hover:bg-teal-500 text-white"
                              )}
                              style={{
                                left: `${bar.leftPct}%`,
                                width: `${bar.widthPct}%`,
                                minWidth: 2,
                                top: 0,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                bar.reservation.reservationPayload && onReservationClick(bar.reservation.reservationPayload as Record<string, unknown>, prop.id, prop.name);
                              }}
                            >
                              <span className="truncate min-w-0">{bar.payload?.guest_name?.trim() || "Reserved"}</span>
                              <span className={cn(
                                "shrink-0 text-[9px] sm:text-[10px] rounded px-0.5 sm:px-1 truncate max-w-[60px] sm:max-w-[80px]",
                                bar.isIcalReservation ? "bg-white/25" : "bg-white/20"
                              )}>{bar.sourceLabel}</span>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs">
                            <p className="font-medium">{bar.sourceLabel}</p>
                            {bar.nights != null && <p>{bar.nights} night{bar.nights !== 1 ? "s" : ""}</p>}
                            {bar.payload?.start_date && bar.payload?.end_date && (
                              <p className="text-muted-foreground text-xs">
                                {format(parseISO(bar.payload.start_date), "MMM d")} – {format(parseISO(bar.payload.end_date), "MMM d")}
                              </p>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                  {/* Turnover day (checkout): chip below thin reservation bar */}
                  {bars.map((bar) => {
                    const hasTurnoverDay = bar.hasTaskOnCheckout || bar.needsTurnoverOnCheckout;
                    if (!hasTurnoverDay) return null;
                    const checkoutIdx = visibleDays.findIndex((d) => format(d, "yyyy-MM-dd") === bar.checkoutStr);
                    if (checkoutIdx < 0) return null;
                    const isFullyAssigned = bar.hasTaskOnCheckout && !!bar.assigneeOnEnd;
                    const taskItem = bar.hasTaskOnCheckout ? checkoutDateToTask.get(bar.checkoutStr) : null;
                    const checkoutDate = bar.checkoutStr ? parseISO(bar.checkoutStr) : null;
                    const dateLabel = checkoutDate ? format(checkoutDate, "EEE, MMM d, yyyy") : bar.checkoutStr;
                    return (
                      <div
                        key={`turnover-${bar.id}`}
                        className="absolute pointer-events-auto flex items-center justify-center"
                        style={{
                          left: `calc(${(checkoutIdx / numCols) * 100}% + 2px)`,
                          width: `calc(${(1 / numCols) * 100}% - 4px)`,
                          top: 4 + RESERVATION_BAR_HEIGHT + 2,
                          height: 36,
                        }}
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (bar.hasTaskOnCheckout && taskItem?.taskPayload) {
                                  onTaskClick(taskItem.taskPayload as CleaningTaskWithRelations, prop.id, prop.name);
                                } else {
                                  bar.reservation.reservationPayload && onTurnoverNeededClick(bar.reservation.reservationPayload as Record<string, unknown>, prop.id, prop.name);
                                }
                              }}
                              className={cn(
                                "rounded text-[10px] font-medium px-1.5 py-0.5 w-full max-w-full min-w-0 flex flex-col items-center justify-center gap-0 truncate overflow-hidden transition-colors",
                                isFullyAssigned
                                  ? "bg-green-600 border border-green-700 text-white hover:bg-green-500"
                                  : "border border-amber-300 bg-amber-100 text-amber-900 hover:bg-amber-200"
                              )}
                            >
                              <span className="truncate w-full text-center">
                                {isFullyAssigned ? "Turnover" : "+ Task"}
                              </span>
                              <span className="text-[9px] opacity-90 font-normal truncate w-full text-center">
                                Assigned to: {bar.assigneeOnEnd ?? "—"}
                              </span>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">
                            {isFullyAssigned ? "Turnover" : "Create Task"} — {dateLabel}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    );
                  })}
                  {/* Standalone tasks (below thin reservation bar) */}
                  {standaloneTasks.map((item) => {
                    const dayStr = item.start.slice(0, 10);
                    const idx = visibleDays.findIndex((d) => format(d, "yyyy-MM-dd") === dayStr);
                    if (idx < 0) return null;
                    const isTurnoverNeeded = item.type === "turnover_needed";
                    const isVendorVisit = item.type === "vendor_visit";
                    const label = isTurnoverNeeded ? "Turnover" : isVendorVisit ? (item.title || "Vendor visit") : (item.title || "Task");
                    const assigneeName = item.type === "task" ? item.assignedToName : isVendorVisit ? "Vendor" : null;
                    const taskDate = dayStr ? parseISO(dayStr) : null;
                    const dateLabel = taskDate ? format(taskDate, "EEE, MMM d, yyyy") : dayStr;
                    return (
                      <div
                        key={item.id}
                        className="absolute pointer-events-auto flex items-center justify-center"
                        style={{
                          left: `calc(${(idx / numCols) * 100}% + 2px)`,
                          width: `calc(${(1 / numCols) * 100}% - 4px)`,
                          top: 4 + RESERVATION_BAR_HEIGHT + 2,
                          height: 36,
                        }}
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isTurnoverNeeded && item.reservationPayload) onTurnoverNeededClick(item.reservationPayload as Record<string, unknown>, prop.id, prop.name);
                                else if (isVendorVisit) { /* Vendor visit: could link to issue via vendorVisitPayload */ }
                                else if (item.taskPayload) onTaskClick(item.taskPayload as CleaningTaskWithRelations, prop.id, prop.name);
                              }}
                              className={cn(
                                "rounded text-[10px] sm:text-[11px] font-medium truncate px-0.5 sm:px-1 py-0.5 flex flex-col items-center justify-center gap-0 w-full max-w-full min-w-0 overflow-hidden transition-colors",
                                isVendorVisit ? "bg-violet-100 text-violet-900 border border-violet-300 hover:bg-violet-200" : isTurnoverNeeded ? "bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200" : "bg-teal-600 text-white hover:bg-teal-500"
                              )}
                            >
                              <span className="truncate w-full text-center">{label}</span>
                              <span className="text-[9px] sm:text-[10px] opacity-90 font-normal truncate w-full text-center">
                                {isVendorVisit ? "Vendor" : `Assigned to: ${assigneeName ?? "—"}`}
                              </span>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">
                            {label} — {dateLabel}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

function DayCell({
  date,
  isWeekend,
  onClick,
}: {
  date: Date;
  isWeekend: boolean;
  onClick: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      className={cn(
        "border-r border-border min-h-[72px] transition-colors cursor-pointer",
        hover && "bg-muted/60",
        isWeekend && "bg-muted/20"
      )}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label={format(date, "EEEE, MMM d")}
    />
  );
}

